import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';
import { User as UserType, UserRank } from '@/types';

interface UserProfileLinkProps {
  userId: string;
  username: string;
  avatarUrl?: string;
  rank?: UserRank;
  size?: 'sm' | 'md' | 'lg';
  showAvatar?: boolean;
  showRank?: boolean;
  className?: string;
  children?: ReactNode;
}

export default function UserProfileLink({
  userId,
  username,
  avatarUrl,
  rank,
  size = 'md',
  showAvatar = true,
  showRank = false,
  className = '',
  children,
}: UserProfileLinkProps) {
  const getAvatarSize = () => {
    switch (size) {
      case 'sm': return 'w-6 h-6';
      case 'lg': return 'w-12 h-12';
      default: return 'w-8 h-8';
    }
  };
  
  const getFontSize = () => {
    switch (size) {
      case 'sm': return 'text-xs';
      case 'lg': return 'text-base';
      default: return 'text-sm';
    }
  };

  const getDisplayRank = (userRank: UserRank) => {
    if (userRank.includes('Super Admin')) {
      // Replace "Super Admin" with "Admin" and keep the collector rank
      return userRank.replace('Super Admin ', 'Admin ');
    }
    return userRank;
  };

  // Return children if provided, otherwise render the default link content
  if (children) {
    return (
      <Link to={`/profile/${username}`} className={`hover:underline ${className}`}>
        {children}
      </Link>
    );
  }

  return (
    <Link 
      to={`/profile/${username}`} 
      className={`flex items-center gap-2 hover:underline ${className}`}
    >
      {showAvatar && (
        <div className={`rounded-full bg-muted flex items-center justify-center overflow-hidden ${getAvatarSize()}`}>
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt={username} 
              className="w-full h-full object-cover"
            />
          ) : (
            <User className={`${size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-6 w-6' : 'h-4 w-4'} text-muted-foreground`} />
          )}
        </div>
      )}
      <div className={`flex ${showRank ? 'flex-col' : 'items-center'}`}>
        <span className={`${getFontSize()} font-medium`}>{username}</span>
        {showRank && rank && (
          <span className={`text-xs text-muted-foreground`}>{getDisplayRank(rank)}</span>
        )}
      </div>
    </Link>
  );
}
